<template>
    <div class="calendar-container" :class="{ 'is-loading': loading }">
        <div class="custom-header">
            <div class="header-left">
                <div class="nav-controls">
                    <button class="btn-nav btn-today" @click="goToToday">Hoje</button>
                    <button class="btn-icon" @click="prev"><i class="fa fa-chevron-left"></i></button>
                    <button class="btn-icon" @click="next"><i class="fa fa-chevron-right"></i></button>
                </div>
                <h2 class="calendar-title">{{ currentTitle }}</h2>
            </div>

            <div class="header-center">
                <input class="date-filter" type="date" v-model="dateToShow" @change="goToDate"/>
            </div>

            <div class="header-right">
                <div class="view-switcher">
                    <button 
                        :class="['btn-view', { active: currentView === 'dayGridDay' }]" 
                        @click="changeView('dayGridDay')"
                    >
                        Dia
                    </button>
                    <button 
                        :class="['btn-view', { active: currentView === 'dayGridWeek' && !calendarOptions.weekends }]" 
                        @click="changeView('dayGridWeek', false)"
                    >
                        Semana Útil
                    </button>
                     <button 
                        :class="['btn-view', { active: currentView === 'dayGridWeek' && calendarOptions.weekends }]" 
                        @click="changeView('dayGridWeek', true)"
                    >
                        Semana
                    </button>
                    <button 
                        :class="['btn-view', { active: currentView === 'dayGridMonth' }]" 
                        @click="changeView('dayGridMonth')"
                    >
                        Mês
                    </button>
                </div>

                <div class="action-buttons">
                    <button class="btn btn-primary--outlined btn-flat" @click="$emit('btnLeftClick')">
                        {{ btnLeft }}
                    </button>
                    <button class="btn btn-primary btn-flat" @click="$emit('btnRightClick')">
                        {{ btnRight }}
                    </button>
                </div>
            </div>
        </div>
        <full-calendar ref="fullCalendar" :options="calendarOptions"/>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
//import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'

export default {
    name: 'PlanningCalendar',
    components: {
        FullCalendar
    },
    props: {
        events: Array,
        btnLeft: String,
        btnRight: String,
        loading: Boolean
    },
    data() {
        return {
            currentTitle: '',
            currentView: 'dayGridMonth',
            dateToShow: '',
            calendarOptions: {
                plugins: [dayGridPlugin, interactionPlugin, listPlugin],
                initialView: 'dayGridMonth',
                height: '100%',
                headerToolbar: false,
                locale: 'pt-br',
                allDayText: 'Dia todo',
                weekends: true,
                selectable: true,
                dayMaxEvents: false,
                views: {
                    dayGridMonth: {
                        expandRows: false,
                        titleFormat: { year: 'numeric', month: 'long' }
                    },
                    dayGridWeek: {
                        expandRows: true,
                        titleFormat: { year: 'numeric', month: 'short', day: 'numeric' }
                    },
                    dayGridDay: {
                        titleFormat: { year: 'numeric', month: 'long', day: 'numeric' }
                    }
                },
                
                buttonText: {
                    today: 'Hoje'
                },
                eventDisplay: 'block',
                events: this.events,
                eventClick: (item) => this.$emit('eventClick', item),
                datesSet: this.handleDatesSet
            }
        }
    },
    methods: {
        handleDatesSet(dateInfo) {
            this.currentTitle = dateInfo.view.title;
            this.currentView = dateInfo.view.type;

            const startDate = this.formatDate(dateInfo.startStr);
            const endDate = this.formatDate(dateInfo.endStr);
            this.$emit('monthChange', { startDate, endDate });
        },

        changeView(viewName, showWeekends = true) {
            const calendarApi = this.$refs.fullCalendar.getApi();
            
            this.calendarOptions.weekends = showWeekends;
            
            calendarApi.changeView(viewName);
        },

        prev() {
            this.$refs.fullCalendar.getApi().prev();
        },

        next() {
            this.$refs.fullCalendar.getApi().next();
        },

        goToToday() {
            this.dateToShow = '';
            this.$refs.fullCalendar.getApi().today();
        },

        goToDate() {
            if (this.dateToShow) {
                const calendarApi = this.$refs.fullCalendar.getApi();
                calendarApi.gotoDate(this.dateToShow);
                calendarApi.changeView('dayGridDay');
            }
        },

        formatDate(dateStr) {
            return dateStr.split('T')[0];
        }
    },
    watch: {
        events() {
            this.calendarOptions.events = [ ...this.events ]
        }
    }
}
</script>

<style scoped lang="sass">
/* Cores APP */
$color-green: #0D7F7B
$color-green-hover: #0a6663
$color-text: #505052
$border-color: #dcdde1

.calendar-container
    height: 100%
    display: flex
    flex-direction: column
    transition: opacity 0.2s ease
    background-color: white

    &.is-loading
        pointer-events: none
        opacity: 0.6
        cursor: wait

/* --- HEADER --- */
.custom-header
    display: flex
    align-items: center
    justify-content: space-between
    padding: 10px 0 15px 0
    flex-wrap: wrap
    gap: 10px

.header-left, .header-center, .header-right
    display: flex
    align-items: center
    gap: 10px

.nav-controls
    display: flex
    align-items: center
    gap: 5px

.calendar-title
    font-size: 1.5rem
    font-weight: normal
    color: $color-text
    margin: 0
    margin-left: 15px
    text-transform: capitalize

/* Botões NAV (Hoje, <, >) */
.btn-nav
    border: 1px solid $border-color
    background-color: white
    color: $color-text
    padding: 5px 15px
    border-radius: 4px
    font-weight: bold
    cursor: pointer
    transition: all 0.2s
    white-space: nowrap

    &:hover
        color: $color-green
        border-color: $color-green

.btn-icon
    background: none
    border: none
    color: $color-text
    font-size: 14px
    width: 30px
    height: 30px
    border-radius: 50%
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    transition: background 0.2s
    flex-shrink: 0

    &:hover
        background-color: rgba(0,0,0,0.05)
        color: $color-green

/* Filtros Data */
.date-filter
    border: 1px solid $color-green
    border-radius: 8px
    padding: 5px 10px
    color: $color-text
    outline: none

/* Switch de View (dia, semana, etc) */
.view-switcher
    display: flex
    border: 1px solid $border-color
    border-radius: 6px
    overflow: hidden

    .btn-view
        background: white
        border: none
        border-right: 1px solid $border-color
        padding: 6px 16px
        cursor: pointer
        color: $color-text
        font-weight: 500
        transition: all 0.2s
        font-size: 14px
        white-space: nowrap

        &:last-child
            border-right: none

        &:hover
            background-color: #f8f9fa
            color: $color-green

        &.active
            background-color: rgba(13, 127, 123, 0.1)
            color: $color-green
            font-weight: bold

/* Botões Ação */
.action-buttons
    display: flex
    gap: 10px
    margin-left: 10px

/* Estilos Botão Base */
.btn-primary
    background-color: $color-green !important
    color: white !important
    border: 1px solid $color-green !important
    
    &--outlined
        background-color: transparent !important
        color: $color-green !important
        border: 1px solid $color-green !important

    &:hover
        opacity: 0.9

.btn-flat
    box-shadow: none !important
    border-radius: 4px

/* --- RESPONSIVIDADE --- */

/* Tablet / Laptop */
@media (max-width: 966px)
    .custom-header
        flex-direction: column
        align-items: flex-start
        
    .header-right
        width: 100%
        justify-content: space-between
        margin-top: 10px

/* Mobile*/
@media (max-width: 640px)
    .custom-header
        gap: 15px

    .header-left
        width: 100%
        justify-content: space-between
        flex-wrap: wrap
    
    .calendar-title
        font-size: 1.2rem
        margin-left: 0
        order: -1
        width: 100%
        margin-bottom: 5px
        text-align: center

    .nav-controls
        width: 100%
        justify-content: center
        
    .header-center
        width: 100%
        .date-filter
            width: 100%
            text-align: center

    .header-right
        flex-direction: column
        align-items: stretch
        gap: 15px

    .action-buttons
        margin-left: 0
        width: 100%
        
        button
            flex: 1

    .view-switcher
        width: 100%
        display: flex
        
        .btn-view
            flex: 1
            padding: 8px 4px
            font-size: 12px
            text-overflow: ellipsis
            overflow: hidden

</style>

<style lang="sass">
/* --- ESTILOS GLOBAIS DO FULLCALENDAR --- */

/* WEEK VIEW */
.fc-dayGridWeek-view 
    .fc-daygrid-day-frame
        display: flex
        flex-direction: column
        height: 100% !important
        overflow: hidden

    .fc-daygrid-day-events
        display: flex
        flex-direction: column
        grid-gap: 6px
        flex-grow: 1
        min-height: 0
        overflow-y: auto !important
        height: auto !important
        margin-bottom: 0 !important
        padding-bottom: 5px

/* MONTH VIEW*/
.fc-dayGridMonth-view 
    .fc-daygrid-day-events
        height: 150px !important
        display: block
        overflow-y: auto !important
        margin-bottom: 2px !important

    .fc-daygrid-day-frame
        height: auto !important
        min-height: 150px !important

/* Scrollbar Calendar */
.fc-daygrid-day-events::-webkit-scrollbar
    width: 4px

.fc-daygrid-day-events::-webkit-scrollbar-thumb
    border-radius: 4px

/* Grid & Texto */
.fc-daygrid-day-top
    justify-content: center !important
    padding-top: 4px

.fc-daygrid-day-number
    color: #454545
    font-size: 14px
    text-decoration: none !important

/* Status e Cor Eventos */
.status
    &-green
        background: #d4f2d4
        border-left: 3px solid #3c7b34
        &:hover
            background: #c3eec3

    &-red
        background: #f6d7d7
        border-left: 3px solid #e60000
        &:hover
            background: #f3c2c2

    &-yellow
        background: #fff3ae
        border-left: 3px solid #f5d92f
        &:hover
            background: #ffeb85

.fc-event
    border: none !important
    border-radius: 2px
    margin: 1px 2px
    padding: 2px 4px
    cursor: pointer
    transition: transform 0.1s

    &:hover
        transform: scale(1.01)

.fc-event-title
    color: #454545
    font-size: 11px
    font-weight: 600
    white-space: pre-wrap

/* Icones nos Eventos */
@mixin icon-mask-sm
    width: 14px
    height: 14px
    background-color: #454545
    display: inline-block
    margin-right: 4px
    vertical-align: middle
    mask-size: contain
    -webkit-mask-size: contain
    mask-repeat: no-repeat
    -webkit-mask-repeat: no-repeat
    mask-position: center
    -webkit-mask-position: center

.icon
    &-harvest:before
        content: ''
        mask: url("/img/icons/svg/harvest.svg") no-repeat center
        -webkit-mask: url("/img/icons/svg/harvest.svg") no-repeat center
        @include icon-mask-sm

    &-planting:before
        content: ''
        mask: url("/img/icons/svg/planting.svg") no-repeat center
        -webkit-mask: url("/img/icons/svg/planting.svg") no-repeat center
        @include icon-mask-sm

    &-pulverization:before
        content: ''
        mask: url("/img/icons/svg/pulverization.svg") no-repeat center
        -webkit-mask: url("/img/icons/svg/pulverization.svg") no-repeat center
        @include icon-mask-sm

    &-soil-preparation:before
        content: ''
        mask: url("/img/icons/svg/soil-preparation.svg") no-repeat center
        -webkit-mask: url("/img/icons/svg/soil-preparation.svg") no-repeat center
        @include icon-mask-sm

    &-seed-treatment:before
        content: ''
        mask: url("/img/icons/svg/seed-treatment.svg") no-repeat center
        -webkit-mask: url("/img/icons/svg/seed-treatment.svg") no-repeat center
        @include icon-mask-sm

    &-fertilization:before
        content: ''
        mask: url("/img/icons/svg/fertilization.svg") no-repeat center
        -webkit-mask: url("/img/icons/svg/fertilization.svg") no-repeat center
        @include icon-mask-sm

    &-other-activity:before
        content: ''
        mask: url("/img/icons/svg/other-activity.svg") no-repeat center
        -webkit-mask: url("/img/icons/svg/other-activity.svg") no-repeat center
        @include icon-mask-sm

</style>